import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  AuthResponse,
  LoginCredentials,
  LoginOutcome,
  MigrationCompletePayload,
  User,
} from '@/types'
import { authApi, isMigrationRequired } from '@/api/auth'
import { useGroupsStore } from '@/stores/groups'

const TOKEN_KEY = 'ma3hub_admin_access_token'
const REFRESH_KEY = 'ma3hub_admin_refresh_token'
const USER_KEY = 'ma3hub_admin_user'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isSuperAdmin = computed(() => user.value?.role === 'SUPER_ADMIN')
  // Admin-portal access: SUPER_ADMIN bypasses + PLATFORM_OPS with admin.access
  // perm gets in. Router guard reads this instead of role-only.
  const canAccessAdminPortal = computed(() => {
    if (user.value?.role === 'SUPER_ADMIN') return true
    return Array.isArray(user.value?.permissions)
      && user.value.permissions.includes('admin.access')
  })

  // Initialize from localStorage
  function init() {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedRefresh = localStorage.getItem(REFRESH_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken) accessToken.value = storedToken
    if (storedRefresh) refreshToken.value = storedRefresh
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        // Invalid stored user
      }
    }

    // Refresh from /auth/me when a token exists so permission grants/revokes
    // applied since the last login propagate within one page load. Without
    // this, `user.permissions` is permanently the snapshot from login.
    if (storedToken) {
      void fetchUser()
      void useGroupsStore().hydrate().catch(() => undefined)
    }
  }

  // Actions

  /** Adopt a session payload — the gate, then persistence.
   *
   *  Shared by the two ways a session can arrive: a plain login, and the last
   *  step of the PIN migration (which hands back the identical payload). The
   *  role/permission gate lives here so neither path can drift from the other.
   */
  function applySession(response: AuthResponse) {
    // SUPER_ADMIN bypasses; PLATFORM_OPS earns access via the admin.access
    // permission. Anyone else is denied.
    const hasAdminAccess = response.user.role === 'SUPER_ADMIN'
      || (Array.isArray(response.user.permissions)
          && response.user.permissions.includes('admin.access'))
    if (!hasAdminAccess) {
      // A PLATFORM_OPS user reaching here has a working credential — they
      // authenticated, or they just finished setting a PIN — and is only
      // missing `admin.access`. Telling them "access denied" reads as a
      // broken PIN and sends them back through the migration they just
      // completed, so name what actually happened instead.
      error.value = response.user.role === 'PLATFORM_OPS'
        ? 'Your support PIN is active. This portal is for platform administration — '
          + 'sign into a sacco dashboard or the mobile app instead.'
        : 'Access denied. Admin portal access required.'
      return false
    }

    accessToken.value = response.access_token
    refreshToken.value = response.refresh_token
    user.value = response.user

    localStorage.setItem(TOKEN_KEY, response.access_token)
    localStorage.setItem(REFRESH_KEY, response.refresh_token)
    localStorage.setItem(USER_KEY, JSON.stringify(response.user))

    void useGroupsStore().hydrate().catch(() => undefined)

    return true
  }

  async function login(credentials: LoginCredentials): Promise<LoginOutcome> {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.login(credentials)

      // Credential accepted, session withheld: the caller has to run the PIN
      // migration before a token exists. Not an error — the login form keeps
      // the credential in memory and continues into the stepper.
      if (isMigrationRequired(response)) {
        return { status: 'migration_required', hasPhone: response.has_phone }
      }

      return applySession(response) ? { status: 'success' } : { status: 'error' }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      return { status: 'error' }
    } finally {
      loading.value = false
    }
  }

  /** Final step of the PIN migration: verify the code, set the PIN, sign in. */
  async function completeMigration(payload: MigrationCompletePayload) {
    loading.value = true
    error.value = null

    try {
      return applySession(await authApi.migrationComplete(payload))
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Could not set your PIN'
      return false
    } finally {
      loading.value = false
    }
  }

  async function fetchUser() {
    try {
      user.value = await authApi.getMe()
      localStorage.setItem(USER_KEY, JSON.stringify(user.value))
    } catch {
      // Ignore errors
    }
  }

  function logout() {
    user.value = null
    accessToken.value = null
    refreshToken.value = null

    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
    useGroupsStore().reset()
  }

  // Initialize on store creation
  init()

  return {
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    isAuthenticated,
    isSuperAdmin,
    canAccessAdminPortal,
    login,
    completeMigration,
    fetchUser,
    logout,
  }
})
